<!-- Narrow on purpose. This is one input and one answer, and the dialog panel sizes to its
     content -- `mat-dialog-container` carries no width of its own, only `max-w-full`. -->
<div class="flex flex-col p-20 gap-y-15" style="width: 34rem; max-width: 90vw">
  <div class="flex flex-col gap-y-2">
    <h2 class="text-lg font-bold text-gray-darkest">Shares from an amount</h2>
    <p class="text-sm text-gray">
      <span class="font-semibold text-gray-darkest">{{ data.ticker }}</span>
      at {{ data.price | currency }} per share
      @if (data.priceAsOf) {
        <span class="text-gray-light">&middot; priced {{ data.priceAsOf | date: 'MMM d, h:mm a' }}</span>
      }
    </p>
    @if (data.description) {
      <p class="text-xs text-gray-dark">{{ data.description }}</p>
    }
  </div>

  <div class="flex flex-col gap-y-5">
    <label class="text-sm font-semibold text-gray-darkest" for="calc-amount">
      {{ isSell() ? 'Amount to raise' : 'Amount to spend' }}
    </label>
    <!-- The $ sits outside the box, as it does on every other money input in this wizard: a
         number input cannot carry a prefix, and typing one would make the value non-numeric. -->
    <div class="flex items-center gap-x-5">
      <span class="text-gray-darkest">$</span>
      <input id="calc-amount" type="number" min="0" step="any"
        class="text-right input is-sm no-spinner" style="width: 16rem"
        appSelectOnFocus appTwoDecimals
        (input)="onAmount($event)"
        [attr.aria-label]="isSell() ? 'Amount to raise' : 'Amount to spend'" />
    </div>
  </div>

  <!-- The answer, and what it really costs.

       Both figures, not just the share count: rounding down to a whole share can leave the
       result short of the amount typed, and on a high-priced security that gap is worth
       seeing before it is applied rather than after. -->
  <div class="flex flex-col px-15 py-10 rounded gap-y-5 bg-gray-lightest">
    @if (canApply()) {
      <div class="flex items-baseline justify-between">
        <span class="text-sm text-gray-dark">Shares</span>
        <span class="text-lg font-bold text-gray-darkest">{{ shares() | number }}</span>
      </div>
      <div class="flex items-baseline justify-between">
        <span class="text-sm text-gray-dark">{{ isSell() ? 'Estimated proceeds' : 'Estimated cost' }}</span>
        <span class="text-sm font-semibold text-gray-darkest">{{ estimatedCost() | currency }}</span>
      </div>
      <!-- ⚠️ "Short by" when selling, not "Left over". Rounding down to a whole share leaves a
           buy with money unspent, which is harmless — but it leaves a SALE short of the amount
           the advisor set out to raise, which they have to make up from somewhere else. Same
           number, opposite consequence, so it cannot share a label. -->
      @if (shortfall() >= 0.005) {
        <div class="flex items-baseline justify-between">
          <span class="text-xs text-gray">{{ isSell() ? 'Short by' : 'Left over' }}</span>
          <span class="text-xs text-gray">{{ shortfall() | currency }}</span>
        </div>
      }
      <!-- Said plainly, because the share count alone looks like an ordinary answer: the advisor
           asked for more than this position holds and is getting the whole thing. -->
      @if (isCapped()) {
        <p class="text-xs text-gray-dark">
          That is more than this position holds — the whole position is {{ shares() | number }} shares.
        </p>
      }
    } @else {
      <!-- Says which of the two reasons it is, because they need different actions: an empty
           box wants a figure, an amount below one share's price wants a bigger one. -->
      <p class="text-sm text-gray">
        @if (amount() === null) {
          Enter an amount to see the share count.
        } @else {
          {{ amount() | currency }} is less than one share at {{ data.price | currency }}.
        }
      </p>
    }
  </div>

  <div class="flex justify-end gap-x-10">
    <button type="button" class="button is-mint is-inverted is-outlined is-sm"
      (click)="dialogRef.close(null)">
      Cancel
    </button>
    <!-- Names the figure it will apply, so the click is not a guess. -->
    <button type="button" class="button is-mint is-sm" [disabled]="!canApply()" (click)="apply()">
      @if (canApply()) {
        {{ isSell() ? 'Sell' : 'Buy' }} {{ shares() | number }} shares
      } @else {
        {{ isSell() ? 'Sell shares' : 'Buy shares' }}
      }
    </button>
  </div>
</div>
